import React, { useEffect, useState } from 'react';
import { API_URL } from '../../api-config';
import axios from "axios";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import VolumenPorNiveles from './volDiff';
import TicksQuantiles from './ticketsQuantiles';
import VolumeZScoreIntradayChart from './VolumeZScoreIntraday';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

function getWebSocketURL(apiURL) {
  const isSecure = apiURL.startsWith('https://');
  const wsProtocol = isSecure ? 'wss' : 'ws';
  const wsURL = apiURL.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsURL}/trading-api/historical/ws`;
}

const Latam = () => {
  const [volumeSum, setVolumeSum] = useState({ T: 0, P: 0 });
  const [ticks, setTicks] = useState([]);
  const [filter, setFilter] = useState('last_day');
  const [lastTick, setLastTick] = useState(null);
  const [diferenciaVolumen, setDiferenciaVolumen] = useState(0);
  const [precioMax, setPrecioMax] = useState(null);
  const [precioMin, setPrecioMin] = useState(null);

  const getLastTicks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/trading-api/historical/ticks/2000`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const { data } = response;
      setTicks(data);
      setLastTick(data[0]);
    } catch (error) {
      console.error('Error al obtener los últimos ticks:', error);
    }
  };

  const updateVolumeSum = (allTicks) => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Santiago" }));

    const filteredTicks = allTicks.filter(tick => {
      const tickDate = new Date(tick.Datetime);
      switch (filter) {
        case 'last_5_minutes':
          return now - tickDate <= 5 * 60 * 1000;
        case 'last_15_minutes':
          return now - tickDate <= 15 * 60 * 1000;
        case 'last_half_hour':
          return now - tickDate <= 30 * 60 * 1000;
        case 'last_hour':
          return now - tickDate <= 60 * 60 * 1000;
        case 'last_2_hours':
          return now - tickDate <= 2 * 60 * 60 * 1000;
        case 'last_24_hours':
          return now - tickDate <= 24 * 60 * 60 * 1000;
        case 'last_day':
        default:
          return tick.Fecha === now.toLocaleDateString("en-US", { timeZone: "America/Santiago" });
      }
    });

    const sortedTicks = [...filteredTicks].sort(
      (a, b) => new Date(a.Datetime) - new Date(b.Datetime)
    );

    let currentLabel = 'T';
    const volumeTotals = { T: 0, P: 0 };

    sortedTicks.forEach((tick, index) => {
      const previousTick = sortedTicks[index - 1];
      const diferencia = previousTick ? tick.Precio - previousTick.Precio : 0;

      if (index === 0) {
        currentLabel = 'T';
      } else {
        currentLabel = diferencia > 0 ? 'T' : diferencia < 0 ? 'P' : currentLabel;
      }
      volumeTotals[currentLabel] += tick.Volumen;
    });

    const diferenciaVolumen = volumeTotals.T - volumeTotals.P;
    setDiferenciaVolumen(diferenciaVolumen);
    setVolumeSum(volumeTotals);

    const precios = sortedTicks.map(t => t.Precio);
    const max = precios.length ? Math.max(...precios) : null;
    const min = precios.length ? Math.min(...precios) : null;
    setPrecioMax(max);
    setPrecioMin(min);
  };

  useEffect(() => {
    getLastTicks();

    const websocketURL = getWebSocketURL(API_URL);
    const socket = new WebSocket(websocketURL);

    socket.onopen = () => {
      console.log('Conectado al WebSocket');
    };

    socket.onmessage = (event) => {
      try {
        const incomingData = JSON.parse(event.data);
        if (incomingData.type === 'ping') return;

        const receivedData = {
          Fecha: incomingData.datetime.split(" ")[0],
          Precio: incomingData.price,
          Volumen: incomingData.volume,
          Datetime: incomingData.datetime.replace(" ", "T"),
          Hora: incomingData.datetime.split(" ")[1],
        };

        setTicks((prevTicks) => [receivedData, ...prevTicks]);
        setLastTick(receivedData);
      } catch (error) {
        console.error("Error procesando datos del WebSocket:", error);
      }
    };

    socket.onclose = () => {
      console.log('Desconectado del WebSocket');
    };

    socket.onerror = (error) => {
      console.error('Error en el WebSocket:', error);
    };

    return () => {
      socket.close();
    };
  }, []);

  useEffect(() => {
    updateVolumeSum(ticks);
  }, [ticks, filter]);

  const barData = {
    labels: ['T', 'P'],
    datasets: [
      {
        label: 'Volúmenes',
        data: [volumeSum.T, volumeSum.P],
        backgroundColor: ['#34d399', '#f87171'],
        borderColor: ['#10b981', '#ef4444'],
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Sumas de Volúmenes por Etiqueta (T vs P)',
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: 'white',
        font: {
          weight: 'bold'
        },
        formatter: Math.round
      },
    },
  };

  return (
    <div className="min-h-screen bg-[#101523] flex flex-col items-center px-6">
      <div className="w-full bg-gray-800 text-white p-4 mb-4 rounded-lg">
        <h2 className="text-lg font-bold">Último Tick Recibido</h2>
        {lastTick ? (
          <p className="text-sm">
            Precio: {lastTick.Precio}, Volumen: {lastTick.Volumen},
            Hora: {lastTick.Datetime.split("T")[1]}, Fecha: {lastTick.Datetime.split("T")[0]}
          </p>
        ) : (
          <p className="text-sm">Cargando último tick...</p>
        )}
      </div>

      <div
        className={`text-4xl font-semibold mb-4 ${
          diferenciaVolumen >= 0 ? 'text-green-400' : 'text-red-400'
        }`}
      >
        Divergencia: {(diferenciaVolumen / 1_000_000).toFixed(2)}M
      </div>

      {/* Título + Máx/Mín */}
      <div className="w-full flex flex-col md:flex-row md:items-end md:justify-center text-white mb-4">
        {precioMax !== null && precioMin !== null && lastTick && (
          <div className="mt-2 md:mt-0 text-right">
            <p className="text-xl font-semibold flex flex-wrap gap-2">
              Máx: {precioMax.toFixed(2)}{" "}
              <span className="text-red-400">
                ({(lastTick.Precio -  precioMax ).toFixed(2)})
              </span>
              | Mín: {precioMin.toFixed(2)}{" "}
              <span className="text-green-400">
                ({(lastTick.Precio - precioMin ).toFixed(2)})
              </span>
            </p>
          </div>
        )}
      </div>

      <div className="mb-6">
        {[
          ['last_5_minutes', 'Últimos 5 minutos'],
          ['last_15_minutes', 'Últimos 15 minutos'],
          ['last_half_hour', 'Última media hora'],
          ['last_hour', 'Última hora'],
          ['last_24_hours', 'Últimas 24 horas'],
        ].map(([value, label]) => (
          <button
            key={value}
            className={`mx-2 px-4 py-2 rounded ${
              filter === value ? 'bg-blue-700' : 'bg-blue-500'
            } text-white`}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="w-full max-w-md mb-8">
        <Bar data={barData} options={barOptions} />
      </div>

      <div className="w-full  mb-8">
        <VolumenPorNiveles/>
      </div>
        <div className="w-full  mb-8">
        <TicksQuantiles/>
      </div>
      <div className="w-full  mb-8">
        <VolumeZScoreIntradayChart />
      </div>
    </div>
  );
};

export default Latam;
